// Onshape OAuth Authentication Library
// Handles OAuth flow with pop-up + postMessage pattern
//
// SECURITY NOTES:
// - Client ID is public and safe to include in frontend code (it's exposed in OAuth authorization URLs)
// - Client Secret must NEVER be in frontend code - it's stored securely in Firebase secrets
// - Client ID in .env (VITE_ONSHAPE_CLIENT_ID) must match Firebase secret (ONSHAPE_CLIENT_ID) exactly
// - Redirect URI must match exactly what's configured in Onshape OAuth app settings

const ONSHAPE_OAUTH_BASE_URL = 'https://oauth.onshape.com';
const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.fastenai.dev';
const ONSHAPE_CLIENT_ID = import.meta.env.VITE_ONSHAPE_CLIENT_ID || '';
const ONSHAPE_REDIRECT_URI = `${APP_URL}/auth/callback`;

// Check if we're in development mode
const IS_DEV = import.meta.env.DEV || import.meta.env.MODE === 'development';

/**
 * Get Onshape OAuth authorization URL
 * @returns {string} Authorization URL
 * @throws {Error} If ONSHAPE_CLIENT_ID is not configured
 */
export function getOnshapeAuthUrl() {
  if (!ONSHAPE_CLIENT_ID || ONSHAPE_CLIENT_ID.trim() === '') {
    const errorMsg = 'ONSHAPE_CLIENT_ID is not configured. Please create a .env file in web-app/ with VITE_ONSHAPE_CLIENT_ID=your_client_id';
    console.error(errorMsg);
    throw new Error(errorMsg);
  }

  // Validate client ID format (Onshape client IDs are base64-encoded strings)
  // Format: alphanumeric characters, underscores, equals signs (base64 padding)
  // Onshape client IDs can end with = characters (base64 padding)
  const clientIdFormatRegex = /^[a-zA-Z0-9_=]+$/;
  if (!clientIdFormatRegex.test(ONSHAPE_CLIENT_ID.trim())) {
    const errorMsg = 'ONSHAPE_CLIENT_ID has invalid format. Client IDs should contain only alphanumeric characters, underscores, and equals signs (base64 padding).';
    console.error(errorMsg, {
      clientIdLength: ONSHAPE_CLIENT_ID.length,
      clientIdPreview: ONSHAPE_CLIENT_ID.length > 10 
        ? `${ONSHAPE_CLIENT_ID.substring(0, 10)}...` 
        : ONSHAPE_CLIENT_ID,
    });
    throw new Error(errorMsg);
  }

  // Validate redirect URI format (must match exactly what's configured in Onshape)
  const expectedRedirectUri = 'https://app.fastenai.dev/auth/callback';
  if (ONSHAPE_REDIRECT_URI !== expectedRedirectUri) {
    console.warn('[Onshape Auth] Redirect URI mismatch:', {
      current: ONSHAPE_REDIRECT_URI,
      expected: expectedRedirectUri,
    });
  }

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: ONSHAPE_CLIENT_ID,
    redirect_uri: ONSHAPE_REDIRECT_URI,
    scope: 'OAuth2Read OAuth2Write OAuth2ReadPII', // OAuth2Read: read access to documents, OAuth2Write: write access to modify documents, OAuth2ReadPII: read personal information (name, email, etc.)
  });

  const authUrl = `${ONSHAPE_OAUTH_BASE_URL}/oauth/authorize?${params.toString()}`;

  return authUrl;
}

/**
 * Exchange OAuth code for access token
 * Cookie is set automatically by Cloud Function (HttpOnly)
 * @param {string} code - OAuth authorization code
 * @returns {Promise<Object>} Response with user info (no token)
 */
export async function exchangeCodeForToken(code) {
  // Validate code input
  if (!code || typeof code !== 'string' || code.trim().length === 0) {
    throw new Error('Authorization code is required');
  }

  // Validate redirect URI format
  const expectedRedirectUri = 'https://app.fastenai.dev/auth/callback';
  if (ONSHAPE_REDIRECT_URI !== expectedRedirectUri) {
    console.warn('[Onshape Auth] Redirect URI mismatch:', {
      current: ONSHAPE_REDIRECT_URI,
      expected: expectedRedirectUri,
    });
  }

  // This calls the backend/Firebase Function to exchange the code
  // The Cloud Function sets a secure HttpOnly cookie automatically
  // Never expose client secret in frontend code
  const response = await fetch(`${APP_URL}/api/auth/onshape/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Important: include cookies in request
    body: JSON.stringify({ 
      code,
      redirect_uri: ONSHAPE_REDIRECT_URI 
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to exchange code for token' }));
    throw new Error(errorData.error || 'Failed to exchange code for token');
  }

  const result = await response.json();
  
  // Set authentication flag in localStorage
  // Store access token for Authorization header (works better with Firebase Hosting rewrites)
  if (result.success) {
    await setAuthState(true);
    
    // Store access token for API calls (Authorization header)
    if (result.access_token) {
      try {
        sessionStorage.setItem('fastenai_access_token', result.access_token);
      } catch (error) {
        console.error('[Onshape Auth] Failed to store access token:', error);
      }
    }
    
    // Store refresh token if provided (for token refresh)
    if (result.refresh_token) {
      try {
        sessionStorage.setItem('fastenai_refresh_token', result.refresh_token);
      } catch (error) {
        console.error('[Onshape Auth] Failed to store refresh token:', error);
      }
    }
  }
  
  return result;
}

/**
 * Check if Chrome storage API is available
 * @returns {boolean} True if chrome.storage is available
 */
function isChromeStorageAvailable() {
  return typeof chrome !== 'undefined' && 
         chrome.storage && 
         chrome.storage.local &&
         typeof chrome.storage.local.set === 'function';
}

/**
 * Set authentication state in localStorage (or Chrome storage if available)
 * This is a flag to track auth state - actual token is in HttpOnly cookie
 * @param {boolean} authenticated - Authentication state
 */
export async function setAuthState(authenticated) {
  const key = 'fastenai_authenticated';
  const value = authenticated ? 'true' : null;
  
  // Prefer Chrome storage API if available (for extension contexts)
  if (isChromeStorageAvailable()) {
    try {
      if (authenticated) {
        await chrome.storage.local.set({ [key]: 'true' });
      } else {
        await chrome.storage.local.remove(key);
      }
      return;
    } catch (error) {
      console.warn('[Onshape Auth] Chrome storage failed, falling back to localStorage:', error);
    }
  }
  
  // Fallback to localStorage for web contexts
  try {
    if (authenticated) {
      localStorage.setItem(key, 'true');
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error('[Onshape Auth] Failed to set auth state:', error);
  }
}

/**
 * Check if user is authenticated
 * Since HttpOnly cookies can't be read by JavaScript, we use localStorage
 * (or Chrome storage) as a flag. The actual token is stored securely in an HttpOnly cookie.
 * @returns {Promise<boolean>} True if authenticated
 */
export async function isAuthenticated() {
  const key = 'fastenai_authenticated';
  
  // Prefer Chrome storage API if available (for extension contexts)
  if (isChromeStorageAvailable()) {
    try {
      const result = await chrome.storage.local.get(key);
      return result[key] === 'true';
    } catch (error) {
      console.warn('[Onshape Auth] Chrome storage failed, falling back to localStorage:', error);
    }
  }
  
  // Fallback to localStorage for web contexts
  try {
    return localStorage.getItem(key) === 'true';
  } catch (error) {
    console.error('[Onshape Auth] Failed to check auth state:', error);
    return false;
  }
}

/**
 * Synchronous version of isAuthenticated for backward compatibility
 * Note: This will not work with Chrome storage, only localStorage
 * @returns {boolean} True if authenticated (localStorage only)
 */
export function isAuthenticatedSync() {
  try {
    return localStorage.getItem('fastenai_authenticated') === 'true';
  } catch (error) {
    console.error('[Onshape Auth] Failed to check auth state:', error);
    return false;
  }
}

/**
 * Clear authentication state
 * Note: HttpOnly cookie must be cleared by backend endpoint
 * This clears the frontend flag
 */
export async function clearAuthToken() {
  const key = 'fastenai_authenticated';
  
  // Prefer Chrome storage API if available (for extension contexts)
  if (isChromeStorageAvailable()) {
    try {
      await chrome.storage.local.remove(key);
    } catch (error) {
      console.warn('[Onshape Auth] Chrome storage failed, falling back to localStorage:', error);
    }
  }
  
  // Fallback to localStorage for web contexts
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('[Onshape Auth] Failed to clear auth state:', error);
  }
  
  // TODO: Call backend logout endpoint to clear HttpOnly cookie
}

/**
 * Get stored refresh token from sessionStorage
 * @returns {string|null} Refresh token or null if not found
 */
export function getStoredRefreshToken() {
  try {
    return sessionStorage.getItem('fastenai_refresh_token');
  } catch (error) {
    console.error('[Onshape Auth] Error reading refresh token from storage:', error);
    return null;
  }
}

/**
 * Refresh access token using refresh_token
 * @returns {Promise<Object>} Response with new access_token and refresh_token
 */
export async function refreshAccessToken() {
  const refreshToken = getStoredRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available. Please re-authenticate.');
  }

  const response = await fetch(`${APP_URL}/api/auth/onshape/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies
    body: JSON.stringify({
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Failed to refresh token' }));
    throw new Error(errorData.error || 'Failed to refresh token');
  }

  const result = await response.json();
  
  if (result.success) {
    // Store new access token
    if (result.access_token) {
      try {
        sessionStorage.setItem('fastenai_access_token', result.access_token);
      } catch (error) {
        console.error('[Onshape Auth] Failed to store refreshed access token:', error);
      }
    }
    
    // Store new refresh token if provided (some OAuth providers rotate refresh tokens)
    if (result.refresh_token) {
      try {
        sessionStorage.setItem('fastenai_refresh_token', result.refresh_token);
      } catch (error) {
        console.error('[Onshape Auth] Failed to store refreshed refresh token:', error);
      }
    }
  }
  
  return result;
}

/**
 * Trigger re-authentication flow
 * Opens OAuth popup and returns promise that resolves when auth completes
 * @returns {Promise<Object>} Promise that resolves with auth result
 */
export function triggerReAuthentication() {
  return new Promise((resolve, reject) => {
    const popup = openAuthPopup();
    
    if (!popup) {
      reject(new Error('Failed to open authentication popup. Please check popup blocker settings.'));
      return;
    }

    // Listen for auth success message from popup
    const handleMessage = (event) => {
      // Verify origin for security
      if (event.origin !== APP_URL) {
        return;
      }

      if (event.data && event.data.type === 'ONSHAPE_AUTH_SUCCESS') {
        window.removeEventListener('message', handleMessage);
        popup.close();
        
        // Store tokens from message
        if (event.data.access_token) {
          try {
            sessionStorage.setItem('fastenai_access_token', event.data.access_token);
          } catch (error) {
            console.error('[Onshape Auth] Failed to store access token:', error);
          }
        }
        
        if (event.data.refresh_token) {
          try {
            sessionStorage.setItem('fastenai_refresh_token', event.data.refresh_token);
          } catch (error) {
            console.error('[Onshape Auth] Failed to store refresh token:', error);
          }
        }
        
        resolve({
          success: true,
          user: event.data.user,
          access_token: event.data.access_token,
          refresh_token: event.data.refresh_token,
        });
      } else if (event.data && event.data.type === 'ONSHAPE_AUTH_ERROR') {
        window.removeEventListener('message', handleMessage);
        popup.close();
        reject(new Error(event.data.error || 'Authentication failed'));
      }
    };

    window.addEventListener('message', handleMessage);

    // Handle popup closed manually
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
        reject(new Error('Authentication popup was closed'));
      }
    }, 1000);
  });
}

/**
 * Open OAuth pop-up window
 * @returns {Window|null} Pop-up window reference
 */
export function openAuthPopup() {
  const width = 600;
  const height = 700;
  const left = (window.screen.width - width) / 2;
  const top = (window.screen.height - height) / 2;

  return window.open(
    getOnshapeAuthUrl(),
    'onshape-auth',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );
}

